import { SkillSubStates } from '../models/SkillState';
import { PreviousAttempts } from '../models/PreviousAttempts';

export const PASSING_SCORE = 8;

/**
 * Substates that mean the student had already been credited for this skill before the current
 * attempt, in a way the assessment history cannot reveal. Passing the skill challenge now
 * therefore does NOT advance the weekly goal: the backend keys the goal off `StudentSkillDetail`'s
 * first-credit dates and disqualifies a skill whose `FirstInferredViaAssessmentDate`,
 * `FirstInferredViaDiagnosticDate` or `FirstPassedViaExitSlipDate` predates its `FirstPassedViaAssessmentDate`
 * (`StudentSkillDetailFilterBuilder.WasNewlyPassed`).
 *
 * Inference leaves no `SkillAssessment` row on the inferred skill, so the pre-attempt substate is
 * the only signal the client has for it.
 *
 * The discrepancy substates are included because reaching one requires the student to have already
 * been credited for the skill — by inference, or by an earlier assessment pass
 * (`SkillAssessmentInferencer.ProcessFailedStudentSkillAssessment`,
 * `SkillDiagnosticInferencer.DetermineNewSkillSubstate`) — so a disqualifying first-credit date is
 * already set either way.
 *
 * Exit-slip inference is deliberately excluded. That filter never reads
 * `first_inferred_via_exit_slip_date`, so the backend will count a skill first credited by exit-slip
 * inference. Suppressing the celebration for those would desync the client in the other direction.
 */
const PRIOR_CREDIT_SUBSTATES: string[] = [
  SkillSubStates.INFERRED_VIA_DIAGNOSTIC,
  SkillSubStates.INFERRED_VIA_ASSESSMENT,
  SkillSubStates.DISCREPANCY_DIAGNOSTIC_ASSESSMENT,
  SkillSubStates.DISCREPANCY_ASSESSMENT_ASSESSMENT,
  SkillSubStates.DISCREPANCY_DIAGNOSTIC_DIAGNOSTIC,
  SkillSubStates.PASSED_VIA_EXITSLIP,
];

/**
 * Whether the skill had already earned the student credit before this attempt, by a route the
 * attempt history cannot show.
 *
 * @param substateId the skill's substate as of page load, i.e. before the current attempt was
 * submitted. Passing a post-attempt substate would misreport, since passing moves the skill to
 * `PASSED_VIA_ASSESSMENT` and erases the evidence.
 */
export const hasPriorCredit = (substateId: string | null): boolean =>
  !!substateId && PRIOR_CREDIT_SUBSTATES.includes(substateId);

/**
 * Whether the student had already passed this skill's own assessment before the current attempt.
 *
 * @param includesCurrentAttempt whether `attempts` has the just-submitted attempt prepended, as it
 * does everywhere downstream of AssessmentWrapper's `setPreviousAttempts`. The current attempt is
 * never evidence of a *prior* pass, so it is dropped.
 */
export const hadPriorPass = (attempts: PreviousAttempts[], includesCurrentAttempt: boolean): boolean =>
  (includesCurrentAttempt ? attempts.slice(1) : attempts).some(attempt => attempt.score >= PASSING_SCORE);

/**
 * Whether passing this skill now should count towards the weekly goal, and so earn the
 * celebration modal and the +1 on the header counter.
 *
 * Mirrors `WasNewlyPassed` on the backend: a skill only counts the first time the
 * student earns credit for it, by any route the backend recognises. The two halves are checked
 * separately because they come from different data — a previous passing attempt is visible in the
 * attempt history, previous inference only in the substate.
 *
 * Callers showing the complementary "this did not count" message must negate this same function
 * rather than re-deriving it, or the two surfaces can drift apart and a student gets both or
 * neither.
 */
export const isWeeklyGoalEligible = (
  previousAttempts: PreviousAttempts[],
  preAttemptSubstateId: string | null,
  attemptsIncludeCurrent = false,
): boolean => !hadPriorPass(previousAttempts, attemptsIncludeCurrent) && !hasPriorCredit(preAttemptSubstateId);

const NEVER_CREDITED_SUBSTATES: string[] = [
  SkillSubStates.NOT_TESTED,
  SkillSubStates.NOT_PASSED_VIA_ASSESSMENT,
  SkillSubStates.NOT_INFERRED_VIA_DIAGNOSTIC,
];

export const isExitSlipWeeklyGoalEligible = (preInferenceSubstateId: string | null): boolean =>
  !preInferenceSubstateId || NEVER_CREDITED_SUBSTATES.includes(preInferenceSubstateId);
